import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  writeBatch,
} from 'firebase/firestore';
import manifest from '../data/manifest.json';
import { getFirebase } from '../firebase/config';
import { ALBUM_ID, photoIdFromFilename } from './ids';
import type { Album, Photo, Video } from '../types';

const PHOTO_DIR = `albums/${ALBUM_ID}/photos`;
const VIDEO_PATH = `albums/${ALBUM_ID}/video/ceremony.mp4`;

export function catalogFromManifest(): { album: Album; photos: Photo[] } {
  const photos: Photo[] = manifest.photos.map((p) => {
    const id = photoIdFromFilename(p.filename);
    return {
      id,
      filename: p.filename,
      order: p.order,
      width: p.width,
      height: p.height,
      bytes: p.bytes,
      storagePath: `${PHOTO_DIR}/${p.filename}`,
      thumbPath: `${PHOTO_DIR}/${id}_thumb.jpg`,
      displayPath: `${PHOTO_DIR}/${id}_display.jpg`,
      thumbUrl: null,
      displayUrl: null,
      caption: '',
      featured: false,
    };
  });
  const album: Album = {
    albumId: manifest.albumId,
    honoree: manifest.honoree,
    title: manifest.title,
    subtitle: manifest.subtitle,
    date: manifest.date,
    dateLabel: manifest.dateLabel,
    photoCount: photos.length,
    video: {
      id: manifest.video.id,
      filename: manifest.video.filename,
      bytes: manifest.video.bytes,
      title: manifest.video.title,
      storagePath: VIDEO_PATH,
      url: null,
    },
  };
  return { album, photos };
}

function asAlbum(id: string, data: Record<string, unknown>): Album {
  const video = (data.video ?? {}) as Record<string, unknown>;
  return {
    albumId: id,
    honoree: String(data.honoree ?? ''),
    title: String(data.title ?? ''),
    subtitle: String(data.subtitle ?? ''),
    date: String(data.date ?? ''),
    dateLabel: String(data.dateLabel ?? ''),
    photoCount: Number(data.photoCount ?? 0),
    video: {
      id: String(video.id ?? 'ceremony'),
      filename: String(video.filename ?? ''),
      bytes: Number(video.bytes ?? 0),
      title: String(video.title ?? ''),
      storagePath: String(video.storagePath ?? VIDEO_PATH),
      url: typeof video.url === 'string' ? video.url : null,
    },
  };
}

function asPhoto(id: string, data: Record<string, unknown>): Photo {
  return {
    id,
    filename: String(data.filename ?? ''),
    order: Number(data.order ?? 0),
    width: Number(data.width ?? 0),
    height: Number(data.height ?? 0),
    bytes: Number(data.bytes ?? 0),
    storagePath: String(data.storagePath ?? ''),
    thumbPath: String(data.thumbPath ?? ''),
    displayPath: String(data.displayPath ?? ''),
    thumbUrl: typeof data.thumbUrl === 'string' ? data.thumbUrl : null,
    displayUrl: typeof data.displayUrl === 'string' ? data.displayUrl : null,
    caption: String(data.caption ?? ''),
    featured: Boolean(data.featured),
  };
}

export async function loadAlbum(): Promise<{ album: Album; photos: Photo[] } | null> {
  const { db } = getFirebase();
  const albumRef = doc(db, 'albums', ALBUM_ID);
  const snap = await getDoc(albumRef);
  if (!snap.exists()) return null;
  const photosSnap = await getDocs(
    query(collection(db, 'albums', ALBUM_ID, 'photos'), orderBy('order')),
  );
  return {
    album: asAlbum(snap.id, snap.data()),
    photos: photosSnap.docs.map((d) => asPhoto(d.id, d.data())),
  };
}

export async function seedCatalog(): Promise<number> {
  const { db } = getFirebase();
  const { album, photos } = catalogFromManifest();
  const existing = await getDoc(doc(db, 'albums', ALBUM_ID));
  if (existing.exists()) {
    const count = (existing.data().photoCount as number | undefined) ?? 0;
    if (count === photos.length) return 0;
  }
  const batch = writeBatch(db);
  batch.set(doc(db, 'albums', ALBUM_ID), album, { merge: true });
  for (const photo of photos) {
    batch.set(doc(db, 'albums', ALBUM_ID, 'photos', photo.id), photo, { merge: true });
  }
  await batch.commit();
  return photos.length;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export type { Video };
